import React, { useState, useMemo } from 'react';
import BasicInfoForm from './BasicInfoForm';
import NarrativeBuilder from './NarrativeBuilder';
import FollowUpQuestions from './FollowUpQuestions';
import TimelineView from './TimelineView';
import OutputsPanel from './OutputsPanel';
import RightsReminderCard from './RightsReminderCard';
import RiskFlagsPanel from './RiskFlagsPanel';
import MissingInfoChecklist from './MissingInfoChecklist';
import WitnessCapture from './WitnessCapture';
import EvidenceUpload from './EvidenceUpload';
import AnonymizerPanel from './AnonymizerPanel';
import StrategyDetectorPanel from './StrategyDetectorPanel';
import MeetingPrepPanel from './MeetingPrepPanel';
import ResponseStylePanel from './ResponseStylePanel';
import StewardHandoffPanel from './StewardHandoffPanel';
import { analyzeIncident, saveCaseDraft } from '../../services/factFinderApi';
import analyticsTracker from '../../utils/analyticsTracker';

const INITIAL_CASE = {
  mode: 'investigation',
  incident_type: 'discipline_investigation',
  title: '',
  date: '',
  time: '',
  location: '',
  department: '',
  crew: '',
  assignment: '',
  equipment: '',
  supervisor: '',
  narrative: '',
  follow_up_answers: {},
  witnesses: [],
  evidence: [],
};

function InvestigationModePage({ user, onBack, loadedCase }) {
  const [caseData, setCaseData] = useState(() => {
    if (loadedCase?.case_payload) {
      return { ...INITIAL_CASE, ...loadedCase.case_payload };
    }
    return INITIAL_CASE;
  });
  const [analysis, setAnalysis] = useState(() => {
    return loadedCase?.analysis_payload || null;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [savedId, setSavedId] = useState(loadedCase?._id || null);
  const [activeTab, setActiveTab] = useState(loadedCase?.analysis_payload ? 'results' : 'input');

  React.useEffect(() => {
    analyticsTracker.trackFactFinderStarted('investigation');
  }, []);

  const missingItems = useMemo(() => {
    const items = [];
    if (!caseData.date) items.push('Date of incident or meeting');
    if (!caseData.time) items.push('Time of incident or meeting');
    if (!caseData.location) items.push('Location');
    if (!caseData.supervisor) items.push('Who called or questioned you');
    if (!caseData.narrative || caseData.narrative.length < 20) {
      items.push('Your factual description (narrative)');
    }
    return items;
  }, [caseData]);

  async function handleAnalyze() {
    if (!caseData.narrative || caseData.narrative.length < 20) {
      setError('Please provide a narrative describing what happened (at least 20 characters).');
      return;
    }
    setLoading(true);
    setError('');
    analyticsTracker.trackFeatureUse('fact_finder', 'analyze_investigation');
    try {
      const result = await analyzeIncident(caseData);
      if (result.success) {
        setAnalysis(result.analysis);
        setActiveTab('results');
        analyticsTracker.trackFactFinderCompleted('investigation');
      } else {
        setError(result.error || 'Unable to analyze incident right now.');
      }
    } catch (err) {
      setError(err.message || 'Unable to analyze incident right now.');
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveDraft() {
    setLoading(true);
    setError('');
    analyticsTracker.trackCaseCreated('fact_finder', 'investigation');
    try {
      const result = await saveCaseDraft({ caseData, analysis });
      if (result.success) {
        setSavedId(result.case_id);
      } else {
        setError(result.error || 'Unable to save draft right now.');
      }
    } catch (err) {
      setError(err.message || 'Unable to save draft right now.');
    } finally {
      setLoading(false);
    }
  }

  function handleFollowUpChange(answers) {
    setCaseData(prev => ({ ...prev, follow_up_answers: answers }));
  }

  function setWitnesses(witnesses) {
    setCaseData(prev => ({ ...prev, witnesses }));
  }

  function setEvidence(evidence) {
    setCaseData(prev => ({ ...prev, evidence }));
  }

  return (
    <div className="fact-finder-page investigation-mode">
      <div className="ff-container">
        {/* Header */}
        <div className="ff-im-header">
          <div className="ff-im-header-top">
            <button onClick={onBack} className="ff-back-btn" data-testid="back-btn">
              &larr; Back
            </button>
            <div className="ff-im-actions">
              <button
                onClick={handleSaveDraft}
                className="ff-btn-secondary"
                disabled={loading}
                data-testid="save-draft-btn"
              >
                {loading ? 'Saving...' : 'Save Draft'}
              </button>
              <button
                onClick={handleAnalyze}
                className="ff-btn-primary"
                disabled={loading || caseData.narrative.length < 20}
                data-testid="analyze-btn"
              >
                {loading ? 'Analyzing...' : 'Build My Prep Packet'}
              </button>
            </div>
          </div>
          <div className="ff-im-title">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
              <img src="/stewie_front.png" alt="Stewie" data-testid="stewie-logo" className="ff-stewie-avatar" />
              <div className="ff-badge">Investigation Mode</div>
            </div>
            <h1>Prepare for Your Meeting</h1>
            <p>Organize the facts, clean up your notes, and walk in ready.</p>
          </div>
        </div>

        {/* Status Messages */}
        {savedId && (
          <div className="ff-success-message" data-testid="success-message">
            Draft saved successfully. Case ID: {savedId}
          </div>
        )}
        {error && (
          <div className="ff-error-message" data-testid="error-message">
            {error}
          </div>
        )}

        {/* Tab Navigation */}
        {analysis && (
          <div className="ff-tabs">
            <button
              className={`ff-tab ${activeTab === 'input' ? 'active' : ''}`}
              onClick={() => setActiveTab('input')}
            >
              Edit Information
            </button>
            <button
              className={`ff-tab ${activeTab === 'results' ? 'active' : ''}`}
              onClick={() => setActiveTab('results')}
            >
              View Results
            </button>
            <button
              className={`ff-tab ${activeTab === 'strategic' ? 'active' : ''}`}
              onClick={() => setActiveTab('strategic')}
              data-testid="strategic-tools-tab"
            >
              Strategic Tools
            </button>
          </div>
        )}

        {/* Main Content */}
        <div className="ff-im-content">
          <div className="ff-im-main">
            {activeTab === 'input' ? (
              <>
                <RightsReminderCard />
                <BasicInfoForm
                  caseData={caseData}
                  setCaseData={setCaseData}
                  investigationMode={true}
                />
                <NarrativeBuilder
                  caseData={caseData}
                  setCaseData={setCaseData}
                />
                <WitnessCapture
                  witnesses={caseData.witnesses}
                  setWitnesses={setWitnesses}
                />
                <EvidenceUpload
                  evidence={caseData.evidence}
                  setEvidence={setEvidence}
                />

                {analysis?.follow_up_questions?.length > 0 && (
                  <FollowUpQuestions
                    questions={analysis.follow_up_questions}
                    answers={caseData.follow_up_answers}
                    setAnswers={handleFollowUpChange}
                  />
                )}
              </>
            ) : activeTab === 'strategic' ? (
              <>
                <AnonymizerPanel narrative={caseData.narrative} />
                <StrategyDetectorPanel caseData={caseData} />
                <MeetingPrepPanel caseData={caseData} />
                <ResponseStylePanel caseData={caseData} />
                <StewardHandoffPanel caseData={caseData} analysis={analysis} />
              </>
            ) : (
              <>
                <TimelineView
                  timeline={analysis?.timeline || []}
                  onTimelineChange={(updated) => setAnalysis(prev => ({ ...prev, timeline: updated }))}
                />
                <OutputsPanel analysis={analysis} caseData={caseData} onExportTracked={() => analyticsTracker.trackFactFinderExportedPdf('investigation')} />
              </>
            )}
          </div>

          {/* Sidebar */}
          <div className="ff-im-sidebar">
            <MissingInfoChecklist items={missingItems} />
            <RiskFlagsPanel
              flags={analysis?.risk_flags || []}
              narrative={caseData.narrative}
              onNarrativeClean={(cleaned) => setCaseData(prev => ({ ...prev, narrative: cleaned }))}
            />
            <div className="ff-reminder-card">
              <h3>Meeting Reminders</h3>
              <ul>
                <li>Stay calm and answer only what you know.</li>
                <li>Do not guess or fill in missing details.</li>
                <li>Keep your answers short, accurate, and factual.</li>
                <li>Review your summary before the meeting begins.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InvestigationModePage;
